import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'zaoxing-diy');

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = articles.filter((a) => a.categorySlug === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.pushState(null, '', `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[{ label: currentCategory.name }]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {currentCategory.articleCount} 篇文章
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={article.cardImageUrl}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime} 分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}